// NEW_FILE_JUSTIFIED: New hook for Gmail email feature — composing queries/mutations/actions from the gmail Convex module. No existing hook covers this domain.
/**
 * useGmailMessages Hook
 * Composes Convex queries, mutations, and actions for the Gmail email
 * feature. Maps raw Convex data to the IGmailEmail / IGmailDraft shapes
 * expected by EmailList, EmailThread, and ComposeEmail components.
 */

import { useQuery, useMutation, useAction } from 'convex/react';
import { useCallback, useMemo } from 'react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import type {
  IGmailEmail,
  IGmailDraft,
  IGmailThread,
  TGmailTab,
  IComposeEmailData,
  IGmailAccess,
  IUseGmailMessagesReturn,
} from '@/@types/gmail';
import { extractFromName } from '@/lib/message-utils';

/** Get the Convex label filter string for a given tab */
function tabToLabel(tab: TGmailTab): string {
  switch (tab) {
    case 'inbox':
      return 'INBOX';
    case 'sent':
      return 'SENT';
    case 'drafts':
      return 'DRAFT';
  }
}

/** Map a raw Convex email row to the IGmailEmail shape */
function mapToGmailEmail(e: {
  _id: string;
  gmailMessageId: string;
  threadId: string;
  labelIds: string[];
  from: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  snippet: string;
  bodyHtml?: string;
  bodyText?: string;
  isRead: boolean;
  hasAttachments: boolean;
  attachments: IGmailEmail['attachments'];
  internalDate: number;
  direction: 'incoming' | 'outgoing';
}): IGmailEmail {
  return {
    id: e._id,
    gmailMessageId: e.gmailMessageId,
    threadId: e.threadId,
    labelIds: e.labelIds,
    from: e.from,
    fromName: extractFromName(e.from),
    to: e.to,
    cc: e.cc,
    bcc: e.bcc,
    subject: e.subject,
    snippet: e.snippet,
    bodyHtml: e.bodyHtml,
    bodyText: e.bodyText,
    isRead: e.isRead,
    hasAttachments: e.hasAttachments,
    attachments: e.attachments,
    internalDate: e.internalDate,
    direction: e.direction,
  };
}

// ============================================================================
// Hook
// ============================================================================

export function useGmailMessages(
  activeTab: TGmailTab,
  pageSize: number,
  selectedThreadId: string | null,
  page: number = 1
): IUseGmailMessagesReturn {
  // Session query
  const rawSession = useQuery(api.gmail.queries.getMyGmailSession, {});

  // Org sessions — used to detect a shared (non-own) active session for
  // members without their own Gmail connection.
  const rawOrgSessions = useQuery(api.gmail.queries.getOrgGmailSessions, {});

  // Emails query — fetch ALL messages (no label filter) so thread grouping
  // can see sent replies alongside inbox messages (Gmail-style threading).
  // Label filtering happens client-side at the thread level.
  const label = tabToLabel(activeTab);
  const fetchLimit = (page + 1) * pageSize * 3;
  const rawEmails = useQuery(
    api.gmail.queries.getEmails,
    activeTab !== 'drafts' ? { limit: fetchLimit } : 'skip'
  );

  // Drafts query (only when drafts tab active)
  const rawDrafts = useQuery(api.gmail.queries.getDrafts, activeTab === 'drafts' ? {} : 'skip');

  // Email count for pagination
  const rawCount = useQuery(
    api.gmail.queries.getEmailCount,
    activeTab !== 'drafts' ? { label } : 'skip'
  );

  // Thread query (when a thread is selected)
  const rawThread = useQuery(
    api.gmail.queries.getThread,
    selectedThreadId !== null ? { threadId: selectedThreadId } : 'skip'
  );

  // Mutations & actions
  const markThreadReadMutation = useMutation(api.gmail.mutations.markThreadRead);
  const sendEmailAction = useAction(api.gmail.actions.sendEmail);
  const saveDraftAction = useAction(api.gmail.actions.saveDraft);
  const fetchAttachmentAction = useAction(api.gmail.actions.fetchAttachment);
  const deleteEmailsAction = useAction(api.gmail.actions.deleteEmails);
  const markEmailsReadStatusAction = useAction(api.gmail.actions.markEmailsReadStatus);
  const syncEmailsAction = useAction(api.gmail.actions.syncEmails);
  const initiateConnectAction = useAction(api.gmail.sessions.initiateConnect);
  const disconnectAction = useAction(api.gmail.sessions.disconnect);

  // Map session
  const session = useMemo(() => {
    if (rawSession === undefined || rawSession === null) return null;
    if (rawSession.status !== 'active') return null;
    return {
      emailAddress: rawSession.emailAddress,
      status: rawSession.status,
    };
  }, [rawSession]);

  // Derive access: own session (full read/write) vs. a shared org session
  // (read-only) vs. neither (connect prompt).
  const access = useMemo((): IGmailAccess => {
    const hasOwnSession = session !== null;
    const sharedSession = rawOrgSessions?.find(s => !s.isOwn && s.status === 'active');
    return {
      hasOwnSession,
      sharedEmailAddress:
        !hasOwnSession && sharedSession !== undefined ? sharedSession.emailAddress : null,
      canWrite: hasOwnSession,
    };
  }, [session, rawOrgSessions]);

  // Map emails to IGmailEmail, group by threadId (Gmail-style threading).
  // All messages are fetched (no label filter), then threads are filtered
  // to only those containing at least one message with the active tab's label.
  // This ensures sent replies appear in thread participant lists ("me, Pavel 2").
  const emails = useMemo((): IGmailEmail[] => {
    if (rawEmails === undefined) return [];

    // Group all emails by threadId
    const threadMap = new Map<
      string,
      {
        newest: IGmailEmail;
        count: number;
        participants: string[];
        hasLabel: boolean;
      }
    >();

    for (const raw of rawEmails) {
      const email = mapToGmailEmail(raw);
      const participantName = email.direction === 'outgoing' ? 'me' : email.fromName;
      const matchesLabel = email.labelIds.includes(label);
      const existing = threadMap.get(email.threadId);

      if (existing === undefined) {
        threadMap.set(email.threadId, {
          newest: email,
          count: 1,
          participants: [participantName],
          hasLabel: matchesLabel,
        });
      } else {
        existing.count++;
        if (!existing.participants.includes(participantName)) {
          existing.participants.push(participantName);
        }
        if (matchesLabel) existing.hasLabel = true;
        // Keep the newest message as the representative
        if (email.internalDate > existing.newest.internalDate) {
          existing.newest = email;
        }
        // Thread is unread if any message in it is unread
        if (!email.isRead) {
          existing.newest = { ...existing.newest, isRead: false };
        }
      }
    }

    // Only show threads that have at least one message with the active label
    const threaded = Array.from(threadMap.values())
      .filter(t => t.hasLabel)
      .map(({ newest, count, participants }) => ({
        ...newest,
        threadMessageCount: count,
        threadParticipants: participants.join(', '),
      }))
      .sort((a, b) => b.internalDate - a.internalDate);

    const startIdx = page * pageSize;
    return threaded.slice(startIdx, startIdx + pageSize);
  }, [rawEmails, page, pageSize, label]);

  // Map drafts to IGmailDraft
  const drafts = useMemo((): IGmailDraft[] => {
    if (rawDrafts === undefined) return [];
    return rawDrafts.map(d => ({
      id: d._id,
      gmailDraftId: d.gmailDraftId,
      to: d.to,
      cc: d.cc,
      bcc: d.bcc,
      subject: d.subject,
      bodyHtml: d.bodyHtml,
      bodyText: d.bodyText,
      hasAttachments: d.hasAttachments,
      updatedAt: d.updatedAt,
    }));
  }, [rawDrafts]);

  // Map thread
  const thread = useMemo((): IGmailThread | null => {
    if (rawThread === undefined || rawThread.length === 0) return null;
    const firstMessage = rawThread[0];
    return {
      threadId: selectedThreadId ?? '',
      subject: firstMessage?.subject ?? '',
      messages: rawThread.map(mapToGmailEmail),
    };
  }, [rawThread, selectedThreadId]);

  // Action wrappers
  const sendEmail = useCallback(
    async (
      data: IComposeEmailData,
      threadId?: string,
      inReplyTo?: string
    ): Promise<{ messageId?: string }> => {
      const result = await sendEmailAction({
        to: data.to,
        cc: data.cc !== undefined && data.cc.length > 0 ? data.cc : undefined,
        bcc: data.bcc !== undefined && data.bcc.length > 0 ? data.bcc : undefined,
        subject: data.subject,
        bodyHtml: data.bodyHtml ?? '',
        threadId,
        inReplyTo,
      });
      return { messageId: result.messageId };
    },
    [sendEmailAction]
  );

  const saveDraft = useCallback(
    async (data: IComposeEmailData, draftId?: string): Promise<{ draftId?: string }> => {
      const result = await saveDraftAction({
        to: data.to,
        cc: data.cc !== undefined && data.cc.length > 0 ? data.cc : undefined,
        bcc: data.bcc !== undefined && data.bcc.length > 0 ? data.bcc : undefined,
        subject: data.subject,
        bodyHtml: data.bodyHtml ?? '',
        draftId,
      });
      return { draftId: result.draftId };
    },
    [saveDraftAction]
  );

  const fetchAttachment = useCallback(
    async (
      gmailMessageId: string,
      attachmentId: string
    ): Promise<{ data: string; mimeType: string; filename: string }> => {
      return await fetchAttachmentAction({ gmailMessageId, attachmentId });
    },
    [fetchAttachmentAction]
  );

  const deleteEmails = useCallback(
    async (gmailMessageIds: string[]): Promise<void> => {
      await deleteEmailsAction({ gmailMessageIds });
    },
    [deleteEmailsAction]
  );

  const markThreadRead = useCallback(
    async (threadId: string): Promise<void> => {
      await markThreadReadMutation({ threadId });
    },
    [markThreadReadMutation]
  );

  const markEmailsReadStatus = useCallback(
    async (gmailMessageIds: string[], markAsRead: boolean): Promise<void> => {
      await markEmailsReadStatusAction({ gmailMessageIds, markAsRead });
    },
    [markEmailsReadStatusAction]
  );

  const syncEmails = useCallback(async (): Promise<void> => {
    await syncEmailsAction({});
  }, [syncEmailsAction]);

  const initiateConnect = useCallback(async (): Promise<{ authUrl: string }> => {
    return await initiateConnectAction({});
  }, [initiateConnectAction]);

  const disconnect = useCallback(async (): Promise<void> => {
    await disconnectAction({});
  }, [disconnectAction]);

  return {
    session,
    isSessionLoading: rawSession === undefined,
    access,
    emails,
    drafts,
    isLoading: activeTab === 'drafts' ? rawDrafts === undefined : rawEmails === undefined,
    hasMore: false, // getEmails returns an array; no pagination cursor in response
    totalCount: rawCount ?? 0,
    thread,
    isThreadLoading: selectedThreadId !== null && rawThread === undefined,
    sendEmail,
    saveDraft,
    fetchAttachment,
    deleteEmails,
    markThreadRead,
    markEmailsReadStatus,
    syncEmails,
    initiateConnect,
    disconnect,
  };
}
